import store from "@/store";
import { getParamQueryTheme, isNumber, isObjEmpty } from "@/utils/utils";

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;

type RootState = typeof store.state & {
  invalidBets?: unknown[];
  nextupStore: { nextupData: unknown[] | Record<string, never> };
  todayStore: { racingEvents: unknown[] };
};

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let shouldReconnect = false;
let wsUrl = "";

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const getFields = (payload) => payload?.field ?? payload?.fields ?? [];

const raceId = (race) => race?.race_external_id ?? race?.external_id;

const sameRace = (race, externalId) =>
  raceId(race) != null && String(raceId(race)) === String(externalId);

const parsePayload = (message) => {
  if (message?.event === "event-updated") return message.data ?? null;
  return getFields(message).length && message?.external_id != null ? message : null;
};

const sortByWinOdd = (fields) =>
  fields.sort((a, b) => {
    const oddA = parseFloat(a.win_odd) || Number.MAX_VALUE;
    const oddB = parseFloat(b.win_odd) || Number.MAX_VALUE;
    if (a.status === "scratched" && b.status !== "scratched") return 1;
    if (a.status !== "scratched" && b.status === "scratched") return -1;
    return oddA - oddB;
  });

const applyFieldUpdates = (race, payload) => {
  const incoming = getFields(payload);
  if (!incoming.length || !race?.fields?.length) return;

  race.fields.forEach((item) => {
    const next = incoming.find((f) => String(f.number) === String(item.number));
    if (!next) return;

    if (next.status && item.status !== next.status) item.status = next.status;

    if (
      isNumber(next.price) &&
      next.price > 0 &&
      parseFloat(item.win_odd) !== parseFloat(next.price)
    ) {
      item.history_price = (item.history_price ?? "").concat(",", next.price);
    }

    if (next.price != null && next.price !== 0) item.win_odd = next.price;
  });

  race.fields = sortByWinOdd(race.fields);
  race.runners = payload.runners || race.runners;
  race.each_way_fraction = payload.each_way_fraction || race.each_way_fraction;
  race.each_way_places = payload.each_way_places || race.each_way_places;
  race.market_groups = payload.market_groups || race.market_groups;

  const byFieldId = new Map(race.fields.map((f) => [f.field_id, f]));
  const syncSelections = (selections) =>
    selections?.forEach((sel) => {
      const field = byFieldId.get(sel?.race_field_ids?.[0]) as { win_odd?: unknown } | undefined;
      if (field?.win_odd != null && field.win_odd !== 0) sel.price = field.win_odd;
    });

  race.race_markets?.forEach((m) => syncSelections(m.selections));
  race.market_groups?.forEach((g) => g.markets?.forEach((m) => syncSelections(m.selections)));
};

const syncCurrentRace = (race) => {
  if (sameRace(store.state.race, raceId(race))) {
    store.commit("SET_RACE", race);
  }
};

const updateNextup = (payload) => {
  const state = store.state as RootState;
  if (!Array.isArray(state.nextupStore.nextupData) || !state.nextupStore.nextupData.length) {
    return;
  }

  const nextup = clone(state.nextupStore.nextupData);
  const index = nextup.findIndex((race) => sameRace(race, payload.external_id));
  if (index === -1) return;

  applyFieldUpdates(nextup[index], payload);
  store.commit("SET_NEXTUP", nextup);
  syncCurrentRace(nextup[index]);
};

const updateToday = (payload) => {
  const state = store.state as RootState;
  if (isObjEmpty(state.todayStore.racingEvents)) return;

  const today = clone(state.todayStore.racingEvents);
  let updatedRace = null;

  today.forEach((item: any) => {
    const races = item?.races?.length ? item.races : sameRace(item, payload.external_id) ? [item] : [];
    races.forEach((race) => {
      if (sameRace(race, payload.external_id)) {
        applyFieldUpdates(race, payload);
        updatedRace = race;
      }
    });
  });

  if (!updatedRace) return;

  store.commit("UPDATE_RACING_EVENTS_DATA", today);
  syncCurrentRace(updatedRace);
};

const handlePayload = (payload) => {
  if (payload?.external_id == null || !getFields(payload).length) return;

  updateNextup(payload);
  updateToday(payload);

  const state = store.state as RootState;
  if (!isObjEmpty(state.betslips)) {
    store.dispatch("updateBetslipFromSocket", {
      currentBetslip: state.betslips,
      invalidBetslip: state.invalidBets ?? [],
      data: payload,
    });
  }
};

const onMessage = (event: MessageEvent) => {
  try {
    const payload = parsePayload(JSON.parse(event.data));
    if (payload) handlePayload(payload);
  } catch (error) {
    console.error("[WebSocket] message parse error:", error);
  }
};

const scheduleReconnect = () => {
  if (!shouldReconnect || !wsUrl) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(connect, delay);
};

const connect = () => {
  if (!wsUrl) return;
  ws?.close();
  ws = new WebSocket(wsUrl);
  ws.onopen = () => {
    reconnectAttempts = 0;
  };
  ws.onmessage = onMessage;
  ws.onerror = (error) => console.error("[WebSocket] error:", error);
  ws.onclose = () => {
    ws = null;
    if (shouldReconnect) scheduleReconnect();
  };
};

const disconnect = () => {
  shouldReconnect = false;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  ws?.close();
  ws = null;
};

export default {
  mounted() {
    const account = getParamQueryTheme("account") || getParamQueryTheme("account_slug");
    if (!account) return;

    const baseUrl = process.env.VUE_APP_SOCKET_URL || "";
    const sep = baseUrl.includes("?") ? "&" : "?";
    wsUrl = `${baseUrl}${sep}account_slug=${encodeURIComponent(account)}`;
    shouldReconnect = true;
    connect();
  },
  beforeDestroy() {
    disconnect();
  },
};
